import { useCallback, useEffect, useState } from "react";
import { Wizard, WizardStep } from "@/components/Wizard";
import { Label } from "@/components/Base/Label";
import { Input } from "@/components/Base/Input";
import {
  useForm,
  FormProvider,
  useFormContext,
  Controller,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Integrators,
  IntegratorsLabel,
  Price,
  StripePromotionCode,
} from "@/types/v2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { useStore } from "@/stores/useStore";
import { httpsCallables } from "@/repositories/httpsCallables";
import { StatusDialog } from "@/components/Dialogs/StatusDialog";
import { priceRepository } from "@/repositories/price";
import { Loading } from "@/components/Page/Loading";
import { StripeSubscription } from "@/components/Stripe";
import { formatAmount, formatInterval } from "@/lib/stripe";
import { Icon } from "@/components/Common/Icon";
import { Spinner } from "@/components/Common/Spinner";
import { sentry } from "@/services/sentry";

// Form validation schema
const formSchema = z.object({
  shopName: z.string().min(1, "Shop name is required"),
  phone: z.string().min(1, "Phone number is required"),
  website: z.string().optional(),
  wantsIntegration: z.string().default("no"),
  integrator: z.string().optional(),
  shopId: z.string().optional(),
  tenantId: z.string().optional(),
  email: z.string().email("Enter a valid email").optional(),
  subscriptionPriceId: z.string().min(1, "A subscription is required"),
  promotionCode: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Form fields for each step
const Step1Fields = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>();

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold">Step 1: Shop Information</h3>
      <div className="space-y-2">
        <Label htmlFor="shopName">Shop Name</Label>
        <Input
          id="shopName"
          placeholder="Enter shop name"
          {...register("shopName")}
        />
        {errors.shopName && (
          <p className="text-sm text-red-500">{errors.shopName.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          placeholder="Enter phone number"
          {...register("phone")}
        />
        {errors.phone && (
          <p className="text-sm text-red-500">{errors.phone.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="website">Website (Optional)</Label>
        <Input
          id="website"
          placeholder="Enter website"
          {...register("website")}
        />
      </div>
    </div>
  );
};

const Step2Fields = () => {
  const { control } = useFormContext<FormValues>();

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold">Step 2: Integration Setup</h3>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="wantsIntegration">
            Would you like to set up an integration?
          </Label>
          <Controller
            name="wantsIntegration"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={(value) => field.onChange(value)}
                value={field.value ?? "no"}
              >
                <SelectTrigger className={"w-48"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"no"}>No</SelectItem>
                  <SelectItem value={"yes"}>Yes</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <p className="text-sm text-primary-foreground">
          Setting up an integration allows Shop Hero to connect with your shop
          management system.
        </p>
      </div>
    </div>
  );
};

const Step3Fields = () => {
  const {
    control,
    watch,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();
  const wantsIntegration = watch("wantsIntegration");
  const selectedIntegrator = watch("integrator");

  if (!wantsIntegration) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold">Step 3: Integration Details</h3>
      <div className="space-y-2">
        <Label htmlFor="integrator">Select Integration</Label>
        <Controller
          name="integrator"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <SelectTrigger>
                <SelectValue placeholder="Select an integration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={Integrators.shopware}>
                  {IntegratorsLabel[Integrators.shopware]}
                </SelectItem>
                <SelectItem value={Integrators.tekmetric}>
                  {IntegratorsLabel[Integrators.tekmetric]}
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {selectedIntegrator && (
        <>
          <div className="space-y-2">
            <Label htmlFor="shopId">Shop ID</Label>
            <Input
              id="shopId"
              placeholder="Enter shop ID"
              {...register("shopId")}
            />
          </div>

          {selectedIntegrator === Integrators.shopware && (
            <div className="space-y-2">
              <Label htmlFor="tenantId">Tenant ID</Label>
              <Input
                id="tenantId"
                placeholder="Enter tenant ID"
                {...register("tenantId")}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email associated with integration</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Step4Footer = ({
  coupon,
  setCoupon,
}: {
  coupon: StripePromotionCode | null;
  setCoupon: (coupon: StripePromotionCode | null) => void;
}) => {
  const { watch, register } = useFormContext<FormValues>();
  const promotionCode = watch("promotionCode");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      // Perform async operation
      if (!promotionCode) {
        setCoupon(null);
        setLoading(false);
        return false;
      }
      const response = await httpsCallables.verifyPromotionCode({
        promotionCode,
      });

      if (response.data.codeValid) {
        setCoupon(response.data.promotionCodes[0] ?? null);
        setLoading(false);
        return true;
      }
      setCoupon(null);
      setLoading(false);
      return false; // Success
    } catch (error) {
      sentry.captureException(error);
      setCoupon(null);
      setLoading(false);
      return false; // Error
    }
  }, [promotionCode, setCoupon]);

  // Debounced effect to trigger handleSubmit after user stops typing
  useEffect(() => {
    if (!promotionCode) {
      setCoupon(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      handleSubmit();
    }, 750); // delay after user stops typing

    return () => clearTimeout(timeoutId);
  }, [handleSubmit, promotionCode, setCoupon]);

  return (
    <div>
      <div className={"flex flex-row items-end justify-start gap-4"}>
        <div className={"relative flex flex-col gap-4"}>
          <Label htmlFor={"promotionCode"}>Promotion Code</Label>
          <Input id={"promotionCode"} {...register("promotionCode")} />
          <div className={"absolute bottom-2 right-2"}>
            {loading ? (
              <Spinner variant={"primary"} size={"sm"} />
            ) : coupon ? (
              <Icon name={"circleCheck"} className={"text-green"} />
            ) : promotionCode !== "" ? (
              <Icon name={"x"} className={"text-red"} />
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <p className="text-sm text-primary-foreground">
          Enter a promotion code to receive a discount on your subscription.
          Coupon will be applied at checkout.
        </p>
      </div>
    </div>
  );
};

const Step4Fields = ({ subscriptions }: { subscriptions: Price[] }) => {
  const { setValue, watch } = useFormContext<FormValues>();

  const handleSelect = (price: Price) => {
    setValue("subscriptionPriceId", price.id ?? "");
  };
  const selectedPrice = watch("subscriptionPriceId");

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold">Step 4: Subscription</h3>
      <p>Choose your subscription from the list below.</p>
      <div className={"grid gap-4 sm:grid-cols-2"}>
        {subscriptions.map((subscription) => (
          <StripeSubscription
            key={subscription.id}
            onSelect={(price) => handleSelect(price)}
            price={subscription}
            selected={selectedPrice === subscription.id}
          />
        ))}
      </div>
    </div>
  );
};

const Step5Fields = ({
  subscriptions,
  coupon,
}: {
  subscriptions: Price[];
  coupon: StripePromotionCode | null;
}) => {
  const { watch } = useFormContext<FormValues>();
  const values = watch();

  const subscription = subscriptions.find(
    (s) => s.id === values.subscriptionPriceId
  );
  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold">Step 5: Confirmation</h3>
      <p>
        Please review your information and click Complete to create your shop.
      </p>
      <div className="space-y-2 rounded-md bg-secondary/20 p-4">
        <p>
          <strong>Shop Name:</strong> {values.shopName}
        </p>
        <p>
          <strong>Phone:</strong> {values.phone}
        </p>
        {values.website && (
          <p>
            <strong>Website:</strong> {values.website}
          </p>
        )}
        <p>
          <strong>Integration:</strong>{" "}
          {values.wantsIntegration === "yes" ? "Yes" : "No"}
        </p>

        {values.wantsIntegration === "yes" && values.integrator && (
          <>
            <p>
              <strong>Integration Type:</strong>{" "}
              {IntegratorsLabel[values.integrator as Integrators]}
            </p>
            {values.shopId && (
              <p>
                <strong>Shop ID:</strong> {values.shopId}
              </p>
            )}
            {values.tenantId && (
              <p>
                <strong>Tenant ID:</strong> {values.tenantId}
              </p>
            )}
            {values.email && (
              <p>
                <strong>Email:</strong> {values.email}
              </p>
            )}
          </>
        )}
        {subscription && (
          <p>
            <strong>Subscription:</strong> {subscription.metadata?.name}
            {" @ "}
            <span className="font-bold">
              {formatAmount(subscription.unit_amount, subscription.currency)}
            </span>
            {subscription.recurring && (
              <span className="ml-1 text-sm text-primary-foreground">
                {formatInterval(subscription.recurring)}
              </span>
            )}
          </p>
        )}
        {coupon && (
          <p>
            <strong>Promotion Code:</strong> {coupon?.code}
            {" --- "}
            <span className="font-bold">
              {coupon.coupon.amount_off
                ? formatAmount(
                    coupon.coupon.amount_off,
                    coupon.coupon.currency ?? "usd"
                  )
                : `${coupon.coupon.percent_off} %`}
            </span>
            <span> off</span>
            {coupon.coupon.duration === "repeating" && (
              <span className="ml-1 text-sm text-primary-foreground">
                for {coupon.coupon.duration_in_months} months
              </span>
            )}
            {coupon.coupon.duration === "forever" && (
              <span className="ml-1 text-sm text-primary-foreground">
                forever
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export const CreateShopWizard = () => {
  const totalSteps = 5;
  const { user } = useStore();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Price[]>([]);
  const [setupFees, setSetupFees] = useState<Price[]>([]);
  const [coupon, setCoupon] = useState<StripePromotionCode | null>(null);

  useEffect(() => {
    setLoading(true);
    priceRepository
      .list()
      .then((products) => {
        setSubscriptions(
          products.flatMap((product) =>
            product.prices.filter(
              (p) =>
                p.metadata.is_setup !== "true" && p.metadata.active === "true"
            )
          )
        );
        setSetupFees(
          products.flatMap((product) =>
            product.prices.filter(
              (p) =>
                p.metadata.is_setup === "true" && p.metadata.active === "true"
            )
          )
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shopName: "",
      phone: "",
      website: "",
      wantsIntegration: "no",
      integrator: "",
      shopId: "",
      tenantId: "",
      email: user?.email,
      promotionCode: "",
    },
  });

  const { handleSubmit, trigger, watch } = methods;
  const wantsIntegration = watch("wantsIntegration");

  const handleNext = async () => {
    // Validate current step before proceeding
    let isValid;

    if (step === 0) {
      isValid = await trigger(["shopName", "phone", "website"]);
    } else if (step === 1) {
      isValid = true; // No validation needed for step 2
    } else if (step === 2 && wantsIntegration === "yes") {
      isValid = await trigger(["integrator", "shopId", "tenantId", "email"]);
    } else if (step === 3) {
      isValid = await trigger(["subscriptionPriceId"]);
    } else {
      isValid = true;
    }

    if (isValid) {
      // Skip step 3 if user doesn't want integration
      if (step === 1 && wantsIntegration === "no") {
        setStep(3); // Jump to confirmation step
      } else if (step < totalSteps - 1) {
        setStep(step + 1);
      }
    }
  };

  const handleBack = () => {
    // If on confirmation step and no integration, go back to step 1
    if (step === 3 && wantsIntegration === "no") {
      setStep(1);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const [progress, setProgress] = useState({
    open: false,
    title: "string",
    currentTask: "string",
    progress: 0,
  });

  const onSubmit = async (data: FormValues) => {
    if (!user) {
      return;
    }

    // If user doesn't want integration, set integrator to manual
    if (data.wantsIntegration === "no") {
      data.integrator = Integrators.manual;
      data.shopId = "";
      data.tenantId = "";
    }

    const email = data.email ?? user?.email;

    if (!email) {
      return;
    }

    const startProcess = async () => {
      setProgress({
        open: true,
        title: "Creating shop...",
        currentTask: "Starting process...",
        progress: 0,
      });

      // Simulate a multi-step process

      const shopData = (await createShop()).data;

      const failureUrl = `${window.location.origin}/onboarding/complete?success=false&shopId=${shopData.id}`;

      const stripeCustomerId = (await createStripeUser(shopData.id))?.data
        ?.stripeCustomerId;
      if (!stripeCustomerId) {
        setProgress((prevState) => ({
          ...prevState,
          open: false,
        }));
        sentry.captureException(
          new Error("Unable to create Stripe customer for " + email)
        );
        window.location.href = failureUrl;
        return;
      }
      await simulateStep("Retrieving pricing information...", 50, 80);
      await simulateStep("Finalizing...", 80, 100);

      // Process complete
      setProgress((prevState) => ({
        ...prevState,
        open: true,
        title: "Linking shop...",
        currentTask: "Process completed successfully!",
        progress: 100,
      }));

      // Wait a moment before closing the dialog
      setTimeout(
        () =>
          setProgress((prevState) => ({
            ...prevState,
            open: false,
          })),
        1500
      );

      const prices = [data.subscriptionPriceId];
      const subscription = subscriptions.find(
        (s) => s.id === data.subscriptionPriceId
      );
      if (subscription?.metadata?.setup_required === "true") {
        const setupPrice = setupFees[0];
        if (setupPrice.id) {
          prices.push(setupPrice.id);
        }
      }

      const session = await httpsCallables.createCheckoutSession({
        priceIds: prices,
        successUrl: `${window.location.origin}/onboarding/complete?success=true&shopId=${shopData.id}`,
        cancelUrl: `${window.location.origin}/onboarding/complete?success=false&shopId=${shopData.id}`,
        shopId: shopData.id,
        customerId: stripeCustomerId,
        couponId: coupon?.coupon?.id,
      });
      window.location.href = session.data.sessionUrl;
    };

    const createShop = async () => {
      await simulateStep("Creating shop...", 0, 20);
      return httpsCallables.registerShop({
        email,
        integrator: data.integrator as Integrators | undefined,
        shopId: data.shopId !== "" ? data.shopId : undefined,
        tenantId: data.tenantId !== "" ? data.tenantId : undefined,
        shopName: data.shopName,
        phone: data.phone,
        website: data.website ?? "",
      });
    };

    const createStripeUser = async (shopId: string) => {
      if (!user?.firstName || !user?.lastName) return;
      await simulateStep("Connecting to Stripe...", 20, 50);

      if (user.stripeCustomerId) {
        return { data: { stripeCustomerId: user.stripeCustomerId } };
      }
      const stripeLookupResult = await httpsCallables.getStripeCustomer();

      if (stripeLookupResult.data?.stripeCustomerId) {
        return stripeLookupResult;
      }

      return await httpsCallables.createStripeCustomer({
        shopId: shopId,
        shopName: data.shopName,
        firstName: user?.firstName,
        lastName: user?.lastName,
      });
    };

    // Helper function to simulate a step in the process
    const simulateStep = async (
      task: string,
      startProgress: number,
      endProgress: number
    ) => {
      setProgress((prevState) => ({
        ...prevState,
        currentTask: task,
      }));

      // Simulate progress updates
      const increment = (endProgress - startProgress) / 10;
      for (let i = 0; i <= 10; i++) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        const newProgress = Math.min(
          startProgress + i * increment,
          endProgress
        );

        setProgress((prevState) => ({
          ...prevState,
          currentTask: task,
          progress: newProgress,
        }));
      }
    };
    startProcess();
  };

  const handleComplete = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <div className="h-[850px] w-[800px]">
      <StatusDialog {...progress} />
      <FormProvider {...methods}>
        <Wizard
          title="Create a Shop"
          totalSteps={totalSteps}
          currentStep={step}
          onNext={handleNext}
          onBack={handleBack}
          onComplete={handleComplete}
          footer={
            step === 3 ? (
              <Step4Footer setCoupon={setCoupon} coupon={coupon} />
            ) : undefined
          }
        >
          <WizardStep active={step === 0}>
            {loading ? <Loading /> : <Step1Fields />}
          </WizardStep>

          <WizardStep active={step === 1}>
            <Step2Fields />
          </WizardStep>

          <WizardStep active={step === 2}>
            <Step3Fields />
          </WizardStep>

          <WizardStep active={step === 3}>
            <Step4Fields subscriptions={subscriptions} />
          </WizardStep>

          <WizardStep active={step === 4}>
            <Step5Fields subscriptions={subscriptions} coupon={coupon} />
          </WizardStep>
        </Wizard>
      </FormProvider>
    </div>
  );
};
